import { useState, useEffect } from 'react';
import { createSecureCookie, isSecureContext } from '@/utils/cookieConfig';

export interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
}

const COOKIE_CONSENT_KEY = 'cookie_consent';
const COOKIE_PREFERENCES_KEY = 'cookie_preferences';
const COOKIE_CONSENT_EVENT = 'cookie-consent';

export function useCookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>({
    necessary: true, // Always true, can't be disabled
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    const applyFromStorage = () => {
      // Check if consent has been given
      const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
      if (!consent) {
        setShowBanner(true);
      } else {
        setShowBanner(false);
        // Load saved preferences
        const savedPreferences = localStorage.getItem(COOKIE_PREFERENCES_KEY);
        if (savedPreferences) {
          try {
            setPreferences(JSON.parse(savedPreferences));
          } catch (error) {
            console.warn('Failed to parse cookie preferences:', error);
          }
        }
      }
    };

    applyFromStorage();

    const handler = (e: Event) => {
      const ce = e as CustomEvent<any>;
      if (ce.detail?.type === 'reset') {
        setShowBanner(true);
        setPreferences({ necessary: true, analytics: false, marketing: false });
      } else if (ce.detail?.type === 'saved') {
        setPreferences(ce.detail.preferences);
        setShowBanner(false);
      } else {
        applyFromStorage();
      }
    };

    window.addEventListener(COOKIE_CONSENT_EVENT, handler as EventListener);

    return () => {
      window.removeEventListener(COOKIE_CONSENT_EVENT, handler as EventListener);
    };
  }, []);

  const acceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true
    };
    saveConsent(allAccepted);
  };

  const acceptNecessary = () => {
    const necessaryOnly = {
      necessary: true,
      analytics: false,
      marketing: false
    };
    saveConsent(necessaryOnly);
  };

  const savePreferences = (newPreferences: CookiePreferences) => {
    saveConsent(newPreferences);
  };

  const saveConsent = (cookiePreferences: CookiePreferences) => {
    setPreferences(cookiePreferences);
    setShowBanner(false);
    
    // Save to localStorage
    localStorage.setItem(COOKIE_CONSENT_KEY, 'true');
    localStorage.setItem(COOKIE_PREFERENCES_KEY, JSON.stringify(cookiePreferences));
    
    // Set secure cookies based on preferences
    if (cookiePreferences.analytics) {
      document.cookie = createSecureCookie('analytics_enabled', 'true');
    }
    
    if (cookiePreferences.marketing) {
      document.cookie = createSecureCookie('marketing_enabled', 'true');
    }
    
    // Always set necessary cookies
    document.cookie = createSecureCookie('necessary_enabled', 'true');

    // Notify other components
    window.dispatchEvent(new CustomEvent(COOKIE_CONSENT_EVENT, { detail: { type: 'saved', preferences: cookiePreferences } }));
  };

  const resetConsent = () => {
    localStorage.removeItem(COOKIE_CONSENT_KEY);
    localStorage.removeItem(COOKIE_PREFERENCES_KEY);
    setShowBanner(true);
    setPreferences({
      necessary: true,
      analytics: false,
      marketing: false
    });

    // Notify other components
    window.dispatchEvent(new CustomEvent(COOKIE_CONSENT_EVENT, { detail: { type: 'reset' } }));
  };

  return {
    showBanner,
    preferences,
    acceptAll,
    acceptNecessary,
    savePreferences,
    resetConsent,
    isSecureContext: isSecureContext()
  };
}